import {
  ArrowRight,
  Users,
  Shield,
  Star,
  Heart,
  Trophy,
  CheckCircle,
} from "lucide-react";

const First100Page = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-pure-white to-electric-blue/10 text-midnight-blue">
      {/* Hero Section */}
      <section className="px-4 py-16 md:py-24">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            {/* Honor Badge */}
            <div className="flex justify-center mb-8">
              <div className="glass-primary p-6 rounded-3xl inline-flex items-center gap-4 animate-neural-pulse">
                <div className="w-16 h-16 bg-gradient-to-br from-electric-blue to-burnt-orange rounded-full flex items-center justify-center">
                  <Trophy className="w-8 h-8 text-white" />
                </div>
                <div className="text-left">
                  <div className="text-sm font-medium text-electric-blue uppercase tracking-wide">
                    Vinh Danh Đặc Biệt
                  </div>
                  <div className="text-2xl font-bold text-midnight-blue">
                    100 Gia Đình Tiên Phong
                  </div>
                </div>
              </div>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 text-midnight-blue leading-tight">
              Cảm Ơn Những Người
              <br />
              <span className="text-burnt-orange relative">
                Dẫn Đầu Hành Trình
                <div className="absolute -bottom-2 left-0 right-0 h-1 bg-burnt-orange/30 rounded-full"></div>
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-midnight-blue/80 mb-8 max-w-4xl mx-auto leading-relaxed">
              Bạn là một trong 100 gia đình đầu tiên tin tưởng và đồng hành cùng
              Locuno trong hành trình xây dựng cộng đồng an toàn. Sự tin tưởng
              của bạn là nguồn động lực lớn nhất để chúng tôi tiếp tục phát
              triển.
            </p>

            {/* Stats */}
            <div className="flex flex-wrap justify-center items-center gap-8 mb-12 text-midnight-blue/70">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-electric-blue" />
                <span className="text-sm font-medium">
                  100 gia đình tiên phong
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-green-600" />
                <span className="text-sm font-medium">98% mức độ hài lòng</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-burnt-orange" />
                <span className="text-sm font-medium">
                  4.9★ đánh giá trung bình
                </span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
              <button className="btn-primary group">
                <div className="flex items-center gap-3">
                  <Heart className="w-5 h-5" />
                  <span>Chia Sẻ Trải Nghiệm Của Bạn</span>
                  <ArrowRight className="w-5 h-5 ml-1 group-hover:translate-x-1 transition-transform" />
                </div>
              </button>
              <button className="btn-secondary">
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5" />
                  <span>Mời Bạn Bè Tham Gia</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Community Showcase Section */}
      <section className="px-4 py-16 bg-gradient-to-r from-electric-blue/5 to-burnt-orange/5">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-midnight-blue">
              Những Câu Chuyện Truyền Cảm Hứng
            </h2>
            <p className="text-lg text-midnight-blue/80 max-w-3xl mx-auto">
              Mỗi gia đình đều có một câu chuyện riêng về cách Locuno mang lại
              sự an tâm và kết nối.
            </p>
          </div>

          {/* Testimonials Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {/* Testimonial 1 */}
            <div className="glass-primary p-6 rounded-2xl hover:glass-secondary transition-all duration-300 group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-electric-blue to-burnt-orange rounded-full flex items-center justify-center text-white font-bold">
                  M
                </div>
                <div>
                  <div className="font-semibold text-midnight-blue">
                    Chị Mai Linh
                  </div>
                  <div className="text-sm text-midnight-blue/70">
                    Mẹ của bé Minh, 12 tuổi
                  </div>
                </div>
              </div>
              <p className="text-midnight-blue/80 italic mb-4">
                "Từ khi dùng Locuno, tôi không còn lo lắng khi con đi học muộn.
                Chỉ cần nhìn vào ứng dụng là biết ngay con đang ở đâu, có an
                toàn không."
              </p>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star
                    key={i}
                    className="w-4 h-4 fill-burnt-orange text-burnt-orange"
                  />
                ))}
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="glass-primary p-6 rounded-2xl hover:glass-secondary transition-all duration-300 group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-electric-blue to-burnt-orange rounded-full flex items-center justify-center text-white font-bold">
                  H
                </div>
                <div>
                  <div className="font-semibold text-midnight-blue">
                    Anh Hoàng
                  </div>
                  <div className="text-sm text-midnight-blue/70">
                    Ba của 2 con
                  </div>
                </div>
              </div>
              <p className="text-midnight-blue/80 italic mb-4">
                "Locuno giúp vợ chồng tôi yên tâm hơn khi cả hai đều bận công
                việc. Biết con an toàn là điều quan trọng nhất."
              </p>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star
                    key={i}
                    className="w-4 h-4 fill-burnt-orange text-burnt-orange"
                  />
                ))}
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="glass-primary p-6 rounded-2xl hover:glass-secondary transition-all duration-300 group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-electric-blue to-burnt-orange rounded-full flex items-center justify-center text-white font-bold">
                  T
                </div>
                <div>
                  <div className="font-semibold text-midnight-blue">
                    Cô Thảo
                  </div>
                  <div className="text-sm text-midnight-blue/70">
                    Giáo viên, mẹ đơn thân
                  </div>
                </div>
              </div>
              <p className="text-midnight-blue/80 italic mb-4">
                "Là mẹ đơn thân, tôi luôn lo lắng về con. Locuno như một người
                bạn đồng hành, giúp tôi theo dõi con mà không làm con cảm thấy
                bị kiểm soát."
              </p>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star
                    key={i}
                    className="w-4 h-4 fill-burnt-orange text-burnt-orange"
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pioneer Benefits Section */}
      <section className="px-4 py-16">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-midnight-blue">
              Đặc Quyền Của Người Tiên Phong
            </h2>
            <p className="text-lg text-midnight-blue/80 max-w-3xl mx-auto">
              Những lợi ích đặc biệt dành riêng cho 100 gia đình đầu tiên tin
              tưởng Locuno.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Benefit 1 */}
            <div className="glass-primary p-8 rounded-2xl hover:bg-electric-blue/10 transition-all duration-300 group">
              <div className="w-16 h-16 bg-gradient-to-br from-electric-blue to-electric-blue/80 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-midnight-blue">
                Miễn Phí Trọn Đời
              </h3>
              <p className="text-midnight-blue/80 mb-4 leading-relaxed">
                100 gia đình đầu tiên sẽ được sử dụng miễn phí vĩnh viễn tất cả
                tính năng cơ bản của Locuno. Đây là lời cảm ơn chân thành nhất
                từ chúng tôi.
              </p>
              <div className="flex items-center gap-2 text-electric-blue font-medium">
                <CheckCircle className="w-5 h-5" />
                <span>Giá trị: 1,188,000 VNĐ/năm</span>
              </div>
            </div>

            {/* Benefit 2 */}
            <div className="glass-primary p-8 rounded-2xl hover:bg-burnt-orange/10 transition-all duration-300 group">
              <div className="w-16 h-16 bg-gradient-to-br from-burnt-orange to-burnt-orange/80 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Star className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-midnight-blue">
                Ưu Tiên Trải Nghiệm
              </h3>
              <p className="text-midnight-blue/80 mb-4 leading-relaxed">
                Được ưu tiên trải nghiệm sớm tất cả tính năng mới, bao gồm
                Health AI Profile (dự kiến ra mắt Q2/2026) trước 6 tháng so với
                người dùng thông thường.
              </p>
              <div className="flex items-center gap-2 text-burnt-orange font-medium">
                <CheckCircle className="w-5 h-5" />
                <span>Beta Testing Priority</span>
              </div>
            </div>

            {/* Benefit 3 */}
            <div className="glass-primary p-8 rounded-2xl hover:bg-electric-blue/10 transition-all duration-300 group">
              <div className="w-16 h-16 bg-gradient-to-br from-electric-blue/80 to-burnt-orange rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-midnight-blue">
                Cộng Đồng VIP
              </h3>
              <p className="text-midnight-blue/80 mb-4 leading-relaxed">
                Tham gia group Facebook/Telegram riêng dành cho 100 gia đình
                tiên phong, tương tác trực tiếp với team phát triển và đóng góp
                ý kiến cho sản phẩm.
              </p>
              <div className="flex items-center gap-2 text-electric-blue font-medium">
                <CheckCircle className="w-5 h-5" />
                <span>Direct Access to Founders</span>
              </div>
            </div>

            {/* Benefit 4 */}
            <div className="glass-primary p-8 rounded-2xl hover:bg-burnt-orange/10 transition-all duration-300 group">
              <div className="w-16 h-16 bg-gradient-to-br from-burnt-orange/80 to-electric-blue rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Heart className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-midnight-blue">
                Ghi Danh Lịch Sử
              </h3>
              <p className="text-midnight-blue/80 mb-4 leading-relaxed">
                Tên gia đình bạn sẽ được ghi trong "Wall of Fame" trên website
                và app Locuno, như một lời tri ân đến những người đã tin tưởng
                từ những ngày đầu.
              </p>
              <div className="flex items-center gap-2 text-burnt-orange font-medium">
                <CheckCircle className="w-5 h-5" />
                <span>Forever Recognition</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="px-4 py-16 bg-gradient-to-r from-electric-blue/10 to-burnt-orange/10">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-midnight-blue">
            Hãy Cùng Lan Tỏa Câu Chuyện Của Chúng Ta
          </h2>
          <p className="text-lg text-midnight-blue/80 mb-12 max-w-2xl mx-auto">
            Mời thêm bạn bè và người thân tham gia Locuno. Mỗi gia đình mới là
            một bước tiến trong việc xây dựng cộng đồng an toàn hơn cho tất cả
            chúng ta.
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-8">
            <button className="btn-primary group">
              <div className="flex items-center gap-3">
                <Heart className="w-5 h-5" />
                <span>Chia Sẻ Với Bạn Bè</span>
                <ArrowRight className="w-5 h-5 ml-1 group-hover:translate-x-1 transition-transform" />
              </div>
            </button>
            <button className="btn-secondary">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5" />
                <span>Tham Gia Cộng Đồng VIP</span>
              </div>
            </button>
          </div>

          {/* Trust Indicators */}
          <div className="flex flex-wrap justify-center items-center gap-6 text-sm text-midnight-blue/60">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-green-700" />
              <span>100% An toàn & Bảo mật</span>
            </div>
            <div className="flex items-center gap-2">
              <Heart className="w-4 h-4 text-burnt-orange" />
              <span>Được 100 gia đình tin dùng</span>
            </div>
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4 text-electric-blue" />
              <span>4.9★ đánh giá</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default First100Page;
